import * as Sentry from '@sentry/react';

// Check if Sentry is configured
const isSentryEnabled = !!process.env.SENTRY_DSN;

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: { message?: string } & Record<string, unknown>;
  };
}

interface ApiRequestDetails {
  url?: string;
  method?: string;
  data?: unknown;
}

/**
 * Report errors to Sentry with additional context
 */
export const reportError = (
  error: unknown,
  context: Record<string, unknown> = {},
  level: Sentry.SeverityLevel = 'error',
) => {
  if (!isSentryEnabled) {
    console.error('Error:', error, 'Context:', context);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    // Add context as extra data
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    Sentry.captureException(typeof error === 'string' ? new Error(error) : error);
  });
};

/**
 * Report API request errors to Sentry
 */
export const reportApiError = (error: ApiErrorLike, requestDetails: ApiRequestDetails) => {
  if (!isSentryEnabled) {
    console.error('API Error:', error);
    return;
  }

  const errorObj = new Error(
    error.response
      ? `API Error ${error.response.status}: ${error.response.data?.message || 'Unknown error'}`
      : 'API request failed'
  );

  reportError(errorObj, {
    statusCode: error.response?.status || 'unknown',
    requestUrl: requestDetails.url,
    requestMethod: requestDetails.method,
    requestData: requestDetails.data,
    responseData: error.response?.data,
  });
};

/**
 * Set user context for Sentry
 */
export const setUserContext = (user: Sentry.User | null) => {
  if (!isSentryEnabled) return;
  Sentry.setUser(user || null);
};
